// Vue
import { ref, watch } from "vue";

// Dependencies
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "vue-toastification";

// Router
import router from "@/router/router";
import { ConstructionFormRoute } from "@/router/constructionRoutes";

// Services
import { getDefaultLCIAResults } from "@/services/Views/CreateMaterial/MaterialTemplate";
import format from "@/services/Utils/format";

// Providers
import { useLibrary } from "@/stores/project/library.store";
import { organizationsStore } from "@/stores/Utils/auth/Organizations";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { newMaterialStore } from "@/stores/Views/CreateMaterial/NewMaterial";
import { useNewConstruction } from "@/stores/Views/CreateConstruction/NewConstructionStore";

// Api
import {
  getMaterial,
  deleteMaterial as deleteMaterialApi,
  deleteConstruction as deleteConstructionApi,
  getConstruction,
} from "@/api/services";

// Types
import { Material } from "@/types/Views/MaterialDatabase/Material";
import { NewMaterial } from "@/types/Views/CreateMaterial/NewMaterial";
import ConstructionAmountCalculator from "@/features/ConstructionAmountCalculator";

interface InspectMaterialToolsProvider {
  // Refs

  // Methods
  deleteMaterialOrConstruction: (materialId: string, isConstruction: boolean) => Promise<void>;
  allowTenantToEdit: (materialTenantId: string) => boolean;
  toggleDeleteModal: (materialId?: string) => void;
  showDeleteModal: (materialId: string) => boolean;

  // Edit/Clone Material Methods
  editMaterial: (materialId: string) => Promise<void>;
  cloneMaterial: (materialId: string) => Promise<void>;

  // Edit/Clone Construction Methods
  editConstruction: (materialId: string, redirect?: boolean) => Promise<void>;
  cloneConstruction: (materialId: string) => Promise<void>;

  // Utility
  getLoading: () => boolean;
  getUpdating: () => boolean;
  getError: () => boolean;
}

const useInspectMaterialTools = defineStore("InspectMaterialToolsProvider", (): InspectMaterialToolsProvider => {
  // epdInfo er tomt hvis custom

  const organizations = organizationsStore();
  const newMaterial = newMaterialStore();
  const authState = useAuthStateStore();
  const { user } = storeToRefs(authState);
  const newConstruction = useNewConstruction();

  const deleteModal = ref<string | null>(null);

  async function deleteMaterialOrConstruction(materialId: string, isConstruction: boolean) {
    // Start loading
    fetchLoading.value = true;

    // On confirm delete, call api to delete material
    const response = isConstruction ? await deleteConstructionApi(materialId) : await deleteMaterialApi(materialId);

    if (response.error) {
      // Stop loading
      fetchLoading.value = false;

      // Show error toast
      useToast().error("Something went wrong!");

      return;
    }

    // Stop loading
    fetchLoading.value = false;

    // Toggle delete modal
    toggleDeleteModal();

    // Show toast
    useToast().success(`${isConstruction ? "Construction" : "Material"} was deleted!`);

    // Remove from cached library list
    useLibrary().removeMaterial(materialId, isConstruction);

    // Re route to library
    router.push({ name: "materialsDatabase" });
  }

  function allowTenantToEdit(materialTenantId: string): boolean {
    return (
      organizations.getCurrentTenant()?.tenantId === materialTenantId ||
      user.value?.roles.map((x) => x.key).includes("PlatformAdmin") ||
      false
    );
  }

  function toggleDeleteModal(materialId?: string) {
    if (deleteModal.value) {
      deleteModal.value = null;
    } else {
      deleteModal.value = materialId || null;
    }
  }

  function showDeleteModal(materialId: string) {
    return deleteModal.value === materialId;
  }

  // ///////////////////
  // Edit/Clone Material Methods
  // ///////////////////

  async function mapMaterialToNewMaterial(material: Material): Promise<NewMaterial> {
    // Get default LCIA results
    const defaultLCIAResults = await getDefaultLCIAResults();

    // Merge default LCIA results with material's LCIA results
    // Always exclude 1 and 2, as these are included in 0 (A1-A3)
    const newLCIAResults = defaultLCIAResults
      .filter((defaultLCIA) => ![1, 2].includes(defaultLCIA.moduleTypeEnum))
      .map((defaultLCIA) => {
        // Find the matching LCIA result from Material, if any
        const materialLCIA = material.lciaResults.find((matLCIA) => matLCIA.moduleTypeEnum === defaultLCIA.moduleTypeEnum);

        if (materialLCIA) {
          // Has value boolean for deciding moduleStatusEnum (value = 2, no value = 0)
          let hasValue = false;

          // Merge default indicators with the material's indicators that have a value
          const mergedIndicators = defaultLCIA.indicators.map((defaultIndicator) => {
            // Find matching indicator from materialLCIA, if any
            const materialIndicator = materialLCIA.indicators.find((matInd) => matInd.type === Number(defaultIndicator.type));

            // If materialIndicator has a value, set hasValue to true
            if (materialIndicator && materialIndicator.value !== null) {
              hasValue = true;
            }

            // Overwrite the default indicator with material's, but only if the material indicator has a value
            return materialIndicator && hasValue
              ? { ...defaultIndicator, value: materialIndicator.value } // overwrite value
              : defaultIndicator; // keep default if no value from material
          });

          // Return the merged LCIAResult
          return {
            ...defaultLCIA,
            moduleStatusEnum: hasValue ? 2 : 0,
            indicators: mergedIndicators,
          };
        }

        // If no matching materialLCIA, return the defaultLCIA as is
        return defaultLCIA;
      });

    // Return the mapped material
    return {
      id: material.id,
      category: material.category,
      tenantId: material.tenantId,
      isPublic: material.isPublic,
      tags: material.tags,
      expectedLifespan: material.expectedLifespan,
      isCustom: material.isCustom,
      name: material.name,
      names: material.names,
      description: material.description,
      descriptions: material.descriptions,
      internalId: material.internalId,
      declaredUnit: {
        declaredUnit: material.declaredUnit.declaredUnit,
        declaredValue: material.declaredUnit.declaredValue,
        mass: material.declaredUnit.mass,
        massUnit: material.declaredUnit.massUnit,
      },
      epdInfo: material.epdInfo
        ? {
            epdSpecificationForm: material.epdInfo.epdSpecificationForm,
            epdProductIndustryType: material.epdInfo.epdProductIndustryType,
            validTo: format.date(material.epdInfo.validTo),
            dataSourceIds: material.epdInfo.datasourceIds || [],
            epdVersion: material.epdInfo.epdVersion || "",
            publicationDate: format.date(material.epdInfo.publicationDate),
            fileEntityId: material.epdInfo.fileEntityId || "",
            link: material.epdInfo.link || "",
            declarationOwnerId: material.epdInfo.declarationOwnerId || null,
            declarationNumber: material.epdInfo.declarationNumber || "",
          }
        : undefined,
      lciaResults: newLCIAResults,
      biogenicParams: material.biogenicParams
        ? { product: material.biogenicParams.product, packaging: material.biogenicParams.packaging }
        : null,
    };
  }

  async function editMaterial(materialId: string) {
    try {
      // Start loading
      fetchLoading.value = true;
      // Load material from api
      const material = await getMaterial(materialId);

      // Transform material to new material
      const mappedMaterial = await mapMaterialToNewMaterial(material);

      // Use newMaterialStore to store the material
      newMaterial.setMaterial(mappedMaterial);

      // Set state for what we are doing (editing) so button actions and text can react on it
      newMaterial.setState("edit");

      // Set is custom
      newMaterial.setIsCustom(material.epdInfo ? false : true);

      // Re route to new material page
      router.push({ name: "createMaterial" });

      // Stop loading
      fetchLoading.value = false;
    } catch (error) {
      console.error(error);

      // Show error toast
      useToast().error("Something went wrong!");

      // Stop loading
      fetchLoading.value = false;
    }
  }

  async function cloneMaterial(materialId: string) {
    try {
      // Start loading
      fetchLoading.value = true;

      // Load material from api
      const material = await getMaterial(materialId);

      // Transform material to new material
      const mappedMaterial = await mapMaterialToNewMaterial(material);

      // Only Real Time LCA publishes EPDs, so a clone made by any other tenant is custom
      const isCustom = !authState.isAdmin || !material.epdInfo;

      // A clone is a new item for the current tenant, so it never inherits the source's public flag
      newMaterial.setMaterial({ ...mappedMaterial, isPublic: false, isCustom });

      // Set is custom
      newMaterial.setIsCustom(isCustom);

      // Set state for what we are doing (cloning) so button actions and text can react on it
      newMaterial.setState("clone");

      // Re route to new material page
      router.push({ name: "createMaterial" });

      // Stop loading
      fetchLoading.value = false;
    } catch (error) {
      console.error(error);

      // Show error toast
      useToast().error("Something went wrong!");

      // Stop loading
      fetchLoading.value = false;
    }
  }

  // ///////////////////
  // Edit/Clone Construction Methods
  // ///////////////////

  async function editConstruction(constructionId: string, redirect: boolean = true) {
    try {
      // Start loading
      fetchLoading.value = true;

      // Load construction from api
      let construction = await getConstruction(constructionId);

      // Calculate input fields for materials in inhomogeneus layers based on their amounts
      construction = {
        ...construction,
        inhomogeneousLayers: construction.inhomogeneousLayers.map((layer) => {
          const materials = layer.materials.map((material) => {
            const result = ConstructionAmountCalculator.fromInhomogeneousLayer(layer, construction.declaredUnit.unitId).calculate(
              material
            );

            return {
              ...material,
              amount: result.amount,
              ...result.dimensions,
              weight: material.weight,
            };
          });
          return { ...layer, materials };
        }),
      };

      // Use newMaterialStore to store the material
      newConstruction.setConstruction(construction);

      // Set name of construction
      newConstruction.setName(construction.names?.[0]?.label ?? "");

      // Set description of construction
      newConstruction.setDescription(construction.descriptions?.[0]?.label ?? "");

      // Set state for what we are doing (editing) so button actions and text can react on it
      newConstruction.setState("edit");

      // Re route to new construction page
      redirect && router.push({ name: ConstructionFormRoute.Edit, params: { id: constructionId } });

      // Stop loading
      fetchLoading.value = false;
    } catch (error) {
      console.error(error);

      // Show error toast
      useToast().error("Something went wrong!");

      fetchLoading.value = false;
    }
  }

  async function cloneConstruction(constructionId: string) {
    try {
      // Start loading
      fetchLoading.value = true;

      // Load material from api
      const construction = await getConstruction(constructionId);

      // Set id to nothing
      construction.id = undefined;

      // Set internal id to nothing
      construction.internalId = undefined;

      // A clone is a new item for the current tenant, so it never inherits the source's public flag
      construction.isPublic = false;

      // Use newMaterialStore to store the material
      newConstruction.setConstruction(construction);

      // Set name of construction
      newConstruction.setName(construction.names?.[0]?.label ?? "");

      // Set description of construction
      newConstruction.setDescription(construction.descriptions?.[0]?.label ?? "");

      // Set state for what we are doing (cloning) so button actions and text can react on it
      newConstruction.setState("clone");

      // Re route to clone construction page
      router.push({ name: ConstructionFormRoute.Clone, params: { id: constructionId } });

      // Stop loading
      fetchLoading.value = false;
    } catch (error) {
      console.error(error);

      // Show error toast
      useToast().error("Something went wrong!");

      // Stop loading
      fetchLoading.value = false;
    }
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const fetchLoading = ref<boolean>(false);
  const fetchError = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const updating = ref<boolean>(false);
  const error = ref<boolean>(false);

  function getError() {
    return error.value;
  }

  function getLoading() {
    return loading.value;
  }

  function getUpdating() {
    return updating.value;
  }

  watch([fetchLoading], ([newLoading1]) => {
    loading.value = newLoading1;
  });

  watch([fetchError], ([newFetchError]) => {
    console.error({ newFetchError });
    error.value = newFetchError;
  });

  // ///////////////////
  // Return
  // ///////////////////

  return {
    // Refs

    // Methods
    deleteMaterialOrConstruction,
    allowTenantToEdit,
    toggleDeleteModal,
    showDeleteModal,

    // Edit/Clone Material Methods
    editMaterial,
    cloneMaterial,

    // Edit/Clone Construction Methods
    editConstruction,
    cloneConstruction,

    // Utility
    getLoading,
    getUpdating,
    getError,
  };
});

export default useInspectMaterialTools;
